import React, { useMemo } from 'react';
import { extractYoutubeId } from '../utils/videoLinks';

interface SafeVideoEmbedProps {
  videoUrl?: string | null;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * 只允许渲染白名单域名（YouTube / 哔哩哔哩）的视频，其余一律不渲染任何 iframe。
 * 后端保存时已经做过同样的域名校验，这里是前端最后一道防线：
 * 不能把任意来源的 URL 直接拼进 iframe src，否则内容管理这个后台权限
 * 就等于可以在客户浏览器里执行任意第三方页面。
 * YouTube ID 的解析逻辑和资源中心列表页取缩略图共用一份（utils/videoLinks），
 * 避免两处正则各写一份、以后改一处漏一处。
 */
function toEmbedUrl(rawUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase();

  if (host === 'youtube.com' || host === 'www.youtube.com' || host === 'm.youtube.com' || host === 'youtu.be') {
    const id = extractYoutubeId(rawUrl);
    return id ? `https://www.youtube.com/embed/${id}` : null;
  }
  if (host === 'bilibili.com' || host === 'www.bilibili.com') {
    // 新版 BV 号
    const bvMatch = url.pathname.match(/\/video\/(BV[\w]+)/i);
    if (bvMatch) return `https://player.bilibili.com/player.html?bvid=${bvMatch[1]}&autoplay=0`;
    // 旧版 av 号（早期视频、部分老链接还在用）
    const avMatch = url.pathname.match(/\/video\/av(\d+)/i);
    if (avMatch) return `https://player.bilibili.com/player.html?aid=${avMatch[1]}&autoplay=0`;
    return null;
  }
  if (host === 'b23.tv') {
    // b23.tv 是短链接，无法在前端直接解析出 BV 号，交由用户改用完整 bilibili.com 链接。
    return null;
  }
  return null;
}

const SafeVideoEmbed: React.FC<SafeVideoEmbedProps> = ({ videoUrl, title, className, style }) => {
  const embedUrl = useMemo(() => (videoUrl ? toEmbedUrl(videoUrl) : null), [videoUrl]);

  if (!videoUrl) return null;

  if (!embedUrl) {
    return (
      <div className={className} style={{ padding: 12, background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, ...style }}>
        <span style={{ color: '#8c8c8c', fontSize: 13 }}>
          该视频链接暂不支持站内播放，请
          <a href={videoUrl} target="_blank" rel="noopener noreferrer" style={{ margin: '0 4px' }}>
            点此在原网站观看
          </a>
        </span>
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{ position: 'relative', paddingTop: '56.25%', background: '#000', borderRadius: 6, overflow: 'hidden', ...style }}
    >
      <iframe
        src={embedUrl}
        title={title || 'video'}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
      />
    </div>
  );
};

export default SafeVideoEmbed;
